import type { MaybePromise } from "@libs/basic/MaybePromise.js";
import { sleep } from "@libs/basic/sleep.js";

export interface Debounced<Args extends unknown[]> {
	abortAll(this: void): void;
	call(this: void, ...args: Args): Promise<void>;
}

export function debounce<Args extends unknown[]>(
	callback: (...args: [AbortSignal, ...Args]) => MaybePromise<void>,
	ms: number,
): Debounced<Args> {
	const queue = new Set<AbortController>();

	function abortAll(reason?: string): void {
		for (const controller of queue) {
			controller.abort(reason);
		}
		queue.clear();
	}

	async function call(...args: Args): Promise<void> {
		abortAll("New debounce call");
		const controller = new AbortController();
		queue.add(controller);

		await sleep(ms);
		if (controller.signal.aborted) {
			return;
		}
		await callback(controller.signal, ...args);
		queue.delete(controller);
	}

	return { abortAll, call };
}
